import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { CsrStatus } from "../types";
import { csrStatusFilterColors, csrStatusValues } from "../utils/tripTypeHelpers";

interface CsrStatusFilterSelectProps {
  value: string; // Can be "all" or a CsrStatus value
  onChange: (value: string) => void;
  /** Translation namespace for labels (e.g., "scheduleList" or "scheduleDashboard") */
  translationNamespace?: "scheduleList" | "scheduleDashboard";
}

/**
 * Reusable CSR status filter dropdown with "All" option.
 * Used for filtering trip lists by CSR status.
 *
 * Usage:
 * ```tsx
 * <CsrStatusFilterSelect
 *   value={statusFilter}
 *   onChange={setStatusFilter}
 *   translationNamespace="scheduleList"
 * />
 * ```
 */
export const CsrStatusFilterSelect: React.FC<CsrStatusFilterSelectProps> = ({
  value,
  onChange,
  translationNamespace = "scheduleList",
}) => {
  const { t } = useTranslation();

  const getLabel = (status: string): string => {
    return t(`screens.schedule.${translationNamespace}.filters.csrStatusOptions.${status}`);
  };

  return (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel>
        {t(`screens.schedule.${translationNamespace}.filters.statusLabel`)}
      </InputLabel>
      <Select
        value={value}
        label={t(`screens.schedule.${translationNamespace}.filters.statusLabel`)}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(val) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {val !== "all" && (
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "3px",
                  bgcolor: csrStatusFilterColors[val as CsrStatus] || "#9e9e9e",
                }}
              />
            )}
            {getLabel(val)}
          </Box>
        )}
      >
        <MenuItem value="all">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getLabel("all")}
          </Box>
        </MenuItem>
        {csrStatusValues.map((status) => (
          <MenuItem key={status} value={status}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "3px",
                  bgcolor: csrStatusFilterColors[status],
                }}
              />
              {getLabel(status)}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
